import { Injectable } from '@angular/core';
import { InterpolatedBlueprintService } from './interpolated-blueprint.service';
import { ColumnBase, FullTemplateInfo, Layout } from './models/blueprint.model';
import { TemplateFieldsCatalog } from './models/template.model';
import { LocaleKeys } from './shared/templates/utils/template-display-sort-order.const';

@Injectable()
export class MapAndSetInterpolationService {
  /**
   * Set of common blueprint layout types that need to be mapped and set,
   * excluding the {@link Layout.MainLayout MainLayout} and the
   * {@link Layout.Cover Cover} due to their own custom logic.
   *
   * If you need to add a new layout, you need to include it in the following
   * places:
   *
   * - {@link Layout}
   * - {@link BlueprintBase}
   */
  private commonLayouts = [
    Layout.Contacts,
    Layout.AdditionalLayout,
    Layout.FloorPlan,
    Layout.AvailabilityFloorplan,
    Layout.RecordsAddressLayout,
    Layout.ThanksPage,
    Layout.Windward,
    Layout.StackingPlanTable,
  ];

  constructor(private interpolatedBlueprintService: InterpolatedBlueprintService) {}

  /**
   * Map and set all the possible layouts that could exist in a blueprint.
   *
   * @param templateId
   * @param entity
   * @param fullTemplateInfo
   * @param columns
   */
  mapAndSet(
    templateId: number,
    entity: string,
    fullTemplateInfo: FullTemplateInfo,
    columns: { [entity: string]: { [columnField: string]: any } },
    localeId: LocaleKeys
  ) {
    this.handleMainLayout(templateId, entity, fullTemplateInfo, columns);
    this.handleCommonLayouts(templateId, entity, fullTemplateInfo, columns, localeId);
    this.handleCoverLayout(templateId, entity, fullTemplateInfo, columns);
  }

  /**
   * Due to the original design, a blueprint did not have any layout before,
   * therefore, to make the logic retro-compatible, we need to handle the
   * {@link Layout.MainLayout MainLayout} different:
   *
   * If the MainLayout is not present in the blueprint, it could mean that it's
   * an old blueprint, therefore, we pass the whole blueprint as the MainLayout.
   *
   * @param templateId
   * @param entity
   * @param fullTemplateInfo
   * @param columns
   */
  private handleMainLayout(
    templateId: number,
    entity: string,
    fullTemplateInfo: FullTemplateInfo,
    columns: { [entity: string]: { [columnField: string]: any } }
  ) {
    const { blueprint, templateCatalog } = fullTemplateInfo;
    const layoutType = Layout.MainLayout;
    const layoutBlueprint: { [columnId: number]: ColumnBase } = blueprint[layoutType] || blueprint;
    this.mapAndSetInterpolation(templateId, entity, layoutType, layoutBlueprint, templateCatalog, columns);
  }

  /**
   * Iterate through all the {@link commonLayouts}, mapping and setting each.
   *
   * @see commonLayouts
   * @param templateId
   * @param entity
   * @param fullTemplateInfo
   * @param columns
   */
  private handleCommonLayouts(
    templateId: number,
    entity: string,
    fullTemplateInfo: FullTemplateInfo,
    columns: { [entity: string]: { [columnField: string]: any } },
    localeId?: LocaleKeys
  ) {
    const { blueprint, templateCatalog } = fullTemplateInfo;

    for (let layoutType of this.commonLayouts) {
      const layoutBlueprint: { [columnId: number]: ColumnBase } = blueprint[layoutType];
      if (layoutBlueprint != null) {
        this.mapAndSetInterpolation(
          templateId,
          entity,
          layoutType,
          layoutBlueprint,
          templateCatalog,
          columns,
          localeId
        );
      }
    }
  }

  /**
   * Map and set the cover blueprint layout using the
   * {@link FullTemplateInfo.coverSlideInfo coverSlideInfo} instead of the
   * actual blueprint object.
   *
   * @param templateId
   * @param entity
   * @param fullTemplateInfo
   * @param columns
   */
  private handleCoverLayout(
    templateId: number,
    entity: string,
    fullTemplateInfo: FullTemplateInfo,
    columns: { [entity: string]: { [columnField: string]: any } }
  ) {
    const { blueprint } = fullTemplateInfo;
    if (blueprint.coverPath != null) {
      const { coverSlideInfo, templateCatalog } = fullTemplateInfo;
      const layoutType = Layout.Cover;
      const layoutBlueprint = coverSlideInfo.mainLayout;
      this.mapAndSetInterpolation(templateId, entity, layoutType, layoutBlueprint, templateCatalog, columns);
    }
  }

  /**
   * Dynamically map and set the interpolation data
   *
   * @param templateId
   * @param entity
   * @param layoutType
   * @param layoutBlueprint
   * @param templateCatalog
   * @param columns
   */
  private mapAndSetInterpolation(
    templateId: number,
    entity: string,
    layoutType: Layout,
    layoutBlueprint: { [columnId: number]: ColumnBase },
    templateCatalog: TemplateFieldsCatalog,
    columns: { [entity: string]: { [columnField: string]: any } },
    localeId?: LocaleKeys
  ) {
    const interpolatedBlueprint = this.interpolatedBlueprintService.mapInterpolatedBlueprint(
      layoutBlueprint,
      templateCatalog,
      columns,
      entity,
      localeId,
      templateId
    );
    this.interpolatedBlueprintService.setInterpolationByTemplateId(templateId, interpolatedBlueprint, layoutType);
  }
}
